import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { observer, inject, propTypes as mobxPropTypes } from 'mobx-react'
import { computed, extendObservable } from 'mobx'
import {
  getFeeItem,
  calculateTotalFeeGrossAmount,
  calculateFeeAmountTaxAmountAndGrossAmount,
  formatDate,
  formatNumber
} from '@atheneum/api/dist/lib'

import Action from '@components/Action'
import AddIcon from '@components/Icon/Add'
import DeleteIcon from '@components/Icon/Delete'
import { showDialog, isCancellationError } from '@components/Dialog'
import ACL from '@components/ACL'
import { Link } from 'react-router-dom'

class AllocatedFees extends Component {
  static propTypes = {
    invoiceStore: PropTypes.object,
    staticData: PropTypes.object,
    invoice: PropTypes.object,
    noActions: PropTypes.bool,
    fees: PropTypes.oneOfType([PropTypes.array, mobxPropTypes.observableArray]),
    onAddFees: PropTypes.func,
    isEditingDisabled: PropTypes.bool,
    growlStore: PropTypes.object
  }

  constructor (props) {
    super(props)
    extendObservable(this, {
      allowInvoiceItemRemoval: computed(
        () => !props.noActions && !props.isEditingDisabled
      )
    })
  }

  removeFeeFromInvoice = (feeId, feeTitle) => {
    return showDialog({
      title: 'Remove this fee from invoice?',
      text: feeTitle,
      type: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete'
    })
      .then(() => {
        this.props.invoiceStore.removeFeeFromInvoice(feeId)
      })
      .catch(error => {
        if (isCancellationError(error)) return
        this.props.growlStore.showError(error)
      })
  }

  renderActions = () => {
    const actions = this.allowInvoiceItemRemoval && (
      <ul className='actions right-list'>
        <Action alignRight>
          <li onClick={this.props.onAddFees}>
            <AddIcon />
            <span>Add fees</span>
          </li>
        </Action>
      </ul>
    )

    return (
      <div className='actions-wrapper'>
        <ACL roles={['Accounting', 'Admin']}>{actions}</ACL>
        <ul className='actions left-list'>
          <h2>Allocated fees</h2>
        </ul>
      </div>
    )
  }

  renderFees = () => {
    const { staticData, fees, invoice } = this.props

    return fees.map(fee => {
      const feeItem = getFeeItem(fee)
      const feeItemType =
        staticData.staticData.names.feeTypes[feeItem.feeTypeId]
      const feeTitle = `${fee.deliverable.name} - ${feeItemType}`
      const readableCurrency = staticData.names.currencies[feeItem.currencyId]
      const {
        netAmount,
        taxAmount,
        grossAmount
      } = calculateFeeAmountTaxAmountAndGrossAmount({ fee, vat: invoice.vat })
      const feeLink = `/project/${fee.projectId}/fee/${fee.id}`

      return (
        <tr key={fee.id}>
          <td className='invoice-column-width-400px'>
            <Link to={feeLink} className='text-wrapper'>
              {feeTitle} <br />
              {fee.expert ? `(${fee.expert.title} ${fee.expert.fullName})` : ''}
            </Link>
          </td>
          <td>
            <span className='text-wrapper'>
              {fee.deliveryDate ? formatDate(fee.deliveryDate) : '-'}
            </span>
          </td>
          <td>
            <span className='text-wrapper'>
              {fee.contract ? fee.contract.contractReference : null}
            </span>
          </td>
          <td>
            <span className='text-wrapper'>
              {formatNumber(netAmount)} {readableCurrency}
            </span>
          </td>
          <td>
            <span className='text-wrapper'>
              {formatNumber(taxAmount)} {readableCurrency}
            </span>
          </td>
          <td className='right-text invoice-column-width-110px'>
            <span className='text-wrapper'>
              {formatNumber(grossAmount)} {readableCurrency}
            </span>
          </td>
          {this.allowInvoiceItemRemoval && (
            <ACL roles={['Accounting', 'Admin']}>
              <td
                className='invoice-column-width-20px'
                onClick={() => this.removeFeeFromInvoice(fee.id, feeTitle)}
              >
                <DeleteIcon />
              </td>
            </ACL>
          )}
        </tr>
      )
    })
  }

  renderNoAllocatedFees = () => {
    return (
      <div>
        {this.renderActions()}
        <h3 className='no-content-message'>No allocated fees</h3>
      </div>
    )
  }

  render () {
    const { fees = [], invoice, staticData } = this.props

    if (!fees.length) return this.renderNoAllocatedFees()

    const totalGrossAmount = calculateTotalFeeGrossAmount({
      fees,
      vat: invoice.vat
    })

    const feeItem = getFeeItem(fees[0])
    const readableCurrency = staticData.names.currencies[feeItem.currencyId]

    return (
      <div>
        {this.renderActions()}
        <table className='table-wrapper--nested invoice-table'>
          <thead>
            <tr>
              <th>Fee type</th>
              <th>Delivery date</th>
              <th>Contract</th>
              <th>Amount</th>
              <th>VAT</th>
              <th className='right-text'>Gross amount</th>

              {this.allowInvoiceItemRemoval && (
                <th className='invoice-column-width-20px' />
              )}
            </tr>
          </thead>

          <tbody>{this.renderFees()}</tbody>

          <tfoot>
            <tr className='title'>
              <td colSpan={5}>Total</td>
              <td className='right-text'>
                {formatNumber(totalGrossAmount)} {readableCurrency}
              </td>

              {this.allowInvoiceItemRemoval && (
                <td className='invoice-column-width-20px' />
              )}
            </tr>
          </tfoot>
        </table>
      </div>
    )
  }
}

export default inject(
  'staticData',
  'invoiceStore',
  'growlStore'
)(observer(AllocatedFees))
